// src/components/dashboard/BookingManager.js - COMPLETE UPDATE with Unified Data Architecture + Enhanced Fixes
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Edit,
  ExternalLink,
  List,
  MessageCircle,
  Phone,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Settings,
  Trash2,
  XCircle
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useBookings } from '../../hooks/useBookings'; // NOW USES ENHANCED VERSION
import { useBusiness } from '../../hooks/useBusiness';
import { useConflictResolution } from '../../hooks/useConflictResolution';
import { useServices } from '../../hooks/useServices';
import CalendarView from './CalendarView';

const BookingManager = () => {
  // ==========================================
  // ENHANCED HOOKS WITH UNIFIED DATA ARCHITECTURE
  // ==========================================
  
  const { 
    user, 
    calendarProvisioningStatus, 
    retryCalendarProvisioning,
    hasCalendarIntegration,
    needsCalendarSetup,
    calendarSetupURL
  } = useAuth(); // Enhanced with calendar provisioning
  
  const { businessProfile } = useBusiness();
  const { services } = useServices();
  
  // ENHANCED useBookings hook with calendar integration
  const {
    bookings,                          // Always available from Firebase
    calendarEvents,                   // Available if calendar integrated
    calendarIntegration,              // Integration status and setup info
    isCalendarIntegrated,             // Boolean flag
    calendarStatus,                   // Current calendar status
    createBooking,                    // Calendar-aware booking creation
    updateBooking,                    // Calendar-aware booking updates
    deleteBooking,                    // Calendar-aware booking deletion
    getCalendarSubscriptionURLs,      // Universal calendar access
    loading: bookingsLoading,         // Unified loading state
    error: bookingsError              // Unified error state
  } = useBookings(user?.businessId);

  // ==========================================
  // EXISTING STATE MANAGEMENT (PRESERVED)
  // ==========================================
  
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ENHANCED: Additional state for improved error handling
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [newBookingData, setNewBookingData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    serviceName: '',
    servicePrice: '',
    duration: '60',
    bookingDate: '',
    time: '',
    notes: ''
  });

  // ==========================================
  // CONFLICT RESOLUTION STATE (PRESERVED)
  // ==========================================
  
  const [conflicts, setConflicts] = useState([]);
  const [suggestedAlternatives, setSuggestedAlternatives] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictBookingData, setConflictBookingData] = useState(null);

  // Memoize the selected date to prevent unnecessary re-renders
  const stableSelectedDate = useMemo(() => selectedDate, [selectedDate]);

  // ==========================================
  // EXISTING CONFLICT RESOLUTION HOOK (PRESERVED)
  // ==========================================
  
  const {
    createBookingWithConflictResolution,
    updateBookingWithConflictResolution,
    checkTimeSlotAvailability,
    conflictResolutionEnabled,
    loading: conflictLoading
  } = useConflictResolution();

  // ==========================================
  // CALENDAR SUBSCRIPTION URLS STATE
  // ==========================================
  
  const [calendarUrls, setCalendarUrls] = useState(null);

  // ==========================================
  // ENHANCED CALENDAR INTEGRATION STATUS DISPLAY
  // ==========================================
  
  const renderCalendarIntegrationStatus = () => {
    // Show calendar provisioning status if available
    if (calendarProvisioningStatus) {
      switch (calendarProvisioningStatus.status) {
        case 'provisioning':
          return (
            <div className="calendar-status provisioning bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                <div>
                  <h3 className="font-medium text-blue-900">Setting Up Your Business Calendar</h3>
                  <p className="text-blue-700 text-sm">{calendarProvisioningStatus.message}</p>
                </div>
              </div>
            </div>
          );
        
        case 'completed':
          return (
            <div className="calendar-status integrated bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <h3 className="font-medium text-green-900">✅ Google Calendar Integrated</h3>
                    <p className="text-green-700 text-sm">External calendar sync active - appointments sync automatically</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      const urls = await getCalendarSubscriptionURLs();
                      if (urls.available) {
                        window.open(urls.urls.googleCalendar, '_blank');
                      }
                    }}
                    className="text-sm px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Calendar
                  </button>
                </div>
              </div>
            </div>
          );
        
        case 'failed':
        case 'manual_required':
          return (
            <div className="calendar-status not-integrated bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  <div>
                    <h3 className="font-medium text-amber-900">⚠️ Calendar Integration Available</h3>
                    <p className="text-amber-700 text-sm">Set up Google Calendar integration for enhanced features</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={retryCalendarProvisioning}
                    className="text-sm px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Retry Auto Setup
                  </button>
                  <Link 
                    to={calendarProvisioningStatus.setupURL || calendarSetupURL} 
                    className="text-sm px-3 py-1 bg-amber-500 text-white rounded hover:bg-amber-600 flex items-center gap-1"
                  >
                    <Settings className="w-3 h-3" />
                    Manual Setup
                  </Link>
                </div>
              </div>
            </div>
          );
        
        default:
          break;
      }
    }

    // Fallback to booking system calendar status
    if (isCalendarIntegrated) {
      return (
        <div className="calendar-status integrated bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div>
                <h3 className="font-medium text-green-900">✅ Google Calendar Integrated</h3>
                <p className="text-green-700 text-sm">External calendar sync active - appointments sync automatically</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  const urls = await getCalendarSubscriptionURLs();
                  if (urls.available) {
                    window.open(urls.urls.googleCalendar, '_blank');
                  }
                }}
                className="text-sm px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                View Calendar
              </button>
              <Link 
                to="/dashboard?tab=calendar-integration"
                className="text-sm px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
              >
                <Settings className="w-3 h-3" />
                Manage
              </Link>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="calendar-status not-integrated bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500" />
              <div>
                <h3 className="font-medium text-blue-900">📅 Enable Calendar Integration</h3>
                <p className="text-blue-700 text-sm">Set up Google Calendar integration for automatic sync and enhanced features</p>
              </div>
            </div>
            <Link 
              to={calendarSetupURL || '/dashboard?tab=calendar-integration'} 
              className="text-sm px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
            >
              <Settings className="w-3 h-3" />
              🔧 Set Up Integration
            </Link>
          </div>
        </div>
      );
    }
  };

  // ==========================================
  // VIEW MANAGEMENT (ENHANCED)
  // ==========================================
  
  // Determine if calendar view should be available
  const canShowCalendarView = useMemo(() => {
    return isCalendarIntegrated || bookings.length > 0;
  }, [isCalendarIntegrated, bookings.length]);

  const handleViewToggle = (newView) => {
    if (newView === 'calendar' && !canShowCalendarView) {
      return; // Prevent switching to calendar view if not available
    }
    setView(newView);
  };

  const renderViewToggle = () => (
    <div className="view-toggle flex gap-2 mb-6">
      <button 
        onClick={() => handleViewToggle('list')}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
          view === 'list' 
            ? 'bg-blue-500 text-white' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        <List className="w-4 h-4" />
        📋 List View
      </button>
      
      <button 
        onClick={() => handleViewToggle('calendar')}
        disabled={!canShowCalendarView}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
          view === 'calendar' 
            ? 'bg-blue-500 text-white' 
            : canShowCalendarView 
              ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        title={!canShowCalendarView ? 'Calendar view available after first booking or calendar setup' : ''}
      >
        <Calendar className="w-4 h-4" />
        📅 Calendar View {!isCalendarIntegrated && '(Local Only)'}
      </button>
    </div>
  );

  // ==========================================
  // ENHANCED BOOKING CARD RENDER FUNCTION (NEW)
  // ==========================================
  
  const renderBookingCard = (booking) => {
  // PRESERVE: All existing customer/service data extraction
  const customerName = booking.customer_name || 
                      booking.customerName || 
                      booking.customerData?.name || 
                      'Unknown Customer';
  
  const serviceName = booking.service_name || 
                     booking.serviceName || 
                     booking.service_type || 
                     'Service';
  
  const customerPhone = booking.customer_whatsapp || 
                       booking.customerPhone || 
                       booking.customerData?.phone || 
                       booking.phone_number || 
                       '';

  const bookingDate = booking.booking_date || booking.date;
  const bookingTime = booking.time;
  const price = booking.service_price || booking.price || 0;
  const status = booking.status || 'confirmed';
  const notes = booking.notes || '';

  // PRESERVE: Status color mapping
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'rescheduled': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // SURGICAL FIX: Replace problematic formatDate with timezone-consistent version
  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    try {
      // SURGICAL FIX: Use the timezone-consistent parsing from useBookings
      if (booking._dateInfo && booking._dateInfo.displayDate) {
        // Use pre-computed display date if available
        return booking._dateInfo.displayDate;
      }
      
      // Fallback: Parse date components to avoid timezone issues
      const dateParts = dateStr.split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0]);
        const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
        const day = parseInt(dateParts[2]);
        const dateObj = new Date(year, month, day);
        
        return dateObj.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
      
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // SURGICAL FIX: Replace problematic formatTime with timezone-consistent version
  const formatTime = (timeStr) => {
    if (!timeStr) return 'No time';
    try {
      // SURGICAL FIX: Use pre-computed display time if available
      if (booking._dateInfo && booking._dateInfo.displayTime) {
        return booking._dateInfo.displayTime;
      }
      
      // Fallback: Direct time parsing without date context
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  // PRESERVE: All existing JSX rendering code exactly as is
  return (
    <div key={booking.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Customer Name & Service - PRIMARY DISPLAY */}
          <div className="flex items-center space-x-3 mb-2">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-lg">
                {customerName}
              </h3>
              <p className="text-gray-600 font-medium">
                {serviceName}
              </p>
            </div>
            
            {/* Status Badge */}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>

          {/* Date & Time - FIXED DISPLAY */}
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              <span>{formatDate(bookingDate)}</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              <span>{formatTime(bookingTime)}</span>
            </div>
            {price > 0 && (
              <div className="flex items-center">
                <DollarSign className="w-4 h-4 mr-1" />
                <span>${price}</span>
              </div>
            )}
          </div>

          {/* PRESERVE: All remaining JSX exactly as in original */}
          {customerPhone && (
            <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
              <Phone className="w-4 h-4" />
              <span>{customerPhone}</span>
              {booking.customerEmail && (
                <>
                  <span>•</span>
                  <span>{booking.customerEmail}</span>
                </>
              )}
            </div>
          )}

          {notes && (
            <div className="mt-2">
              <p className="text-sm text-gray-600 italic">
                <MessageCircle className="w-4 h-4 inline mr-1" />
                {notes}
              </p>
            </div>
          )}

          {booking.calendarSyncStatus && (
            <div className="mt-2 flex items-center text-xs">
              {booking.calendarSyncStatus === 'synced' ? (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  <span>Synced to calendar</span>
                </div>
              ) : (
                <div className="flex items-center text-yellow-600">
                  <RotateCcw className="w-3 h-3 mr-1" />
                  <span>Sync pending</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => setEditingBooking(booking)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit booking"
          >
            <Edit className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => handleDeleteBooking(booking.customerId, booking.id)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete booking"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

  // ==========================================
  // ENHANCED BOOKING MANAGEMENT FUNCTIONS
  // ==========================================
  
  // ENHANCED: Improved create booking function with proper data structure
  const handleCreateBooking = async (bookingData) => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      setLocalError(null);
      setSuccess(null);

      console.log('📝 Creating booking with unified data architecture...', bookingData);

      // FIXED: Ensure all required fields are present and properly formatted
      const formattedBookingData = {
        // Customer information
        customerName: bookingData.customerName || bookingData.customer_name,
        customerPhone: bookingData.customerPhone || bookingData.customer_whatsapp,
        customerWhatsapp: bookingData.customerPhone || bookingData.customer_whatsapp,
        customerEmail: bookingData.customerEmail || '',
        
        // Service information
        serviceName: bookingData.serviceName || bookingData.service_name,
        servicePrice: parseFloat(bookingData.servicePrice || bookingData.price || 0),
        duration: parseInt(bookingData.duration || 60),
        
        // Booking timing
        bookingDate: bookingData.bookingDate || bookingData.booking_date,
        time: bookingData.time,
        
        // Additional information
        notes: bookingData.notes || '',
        notify_customer: bookingData.notify_customer !== false,
        status: 'confirmed',
        
        // Metadata
        created_by: 'dashboard',
        source: 'manual'
      };

      console.log('📋 Formatted booking data:', formattedBookingData);

      // Enhanced booking creation with calendar awareness
      const result = await createBooking(formattedBookingData);
      
      if (result.success) {
        console.log('✅ Booking created successfully:', result.booking);
        setShowCreateModal(false);
        
        // Reset form data
        setNewBookingData({
          customerName: '',
          customerPhone: '',
          customerEmail: '',
          serviceName: '',
          servicePrice: '',
          duration: '60',
          bookingDate: '',
          time: '',
          notes: ''
        });
        
        // Enhanced success message based on calendar integration
        if (isCalendarIntegrated) {
          setSuccess('✅ Booking created and synced to Google Calendar!');
        } else {
          setSuccess('✅ Booking created! Set up calendar integration for automatic sync.');
        }
      } else {
        throw new Error(result.error || 'Failed to create booking');
      }
      
      return result;
    } catch (error) {
      console.error('❌ Booking creation failed:', error);
      setLocalError(error.message);
      setCreateError(error.message);
      throw error;
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateBooking = async (customerId, bookingId, updateData) => {
    try {
      setLocalError(null);
      setSuccess(null);

      console.log('📝 Updating booking with calendar sync...');

      // Enhanced booking update with calendar sync
      const result = await updateBooking(customerId, bookingId, updateData);
      
      if (result.success) {
        setEditingBooking(null);
        
        // Enhanced success message based on calendar integration
        if (isCalendarIntegrated) {
          setSuccess('✅ Booking updated and synced to Google Calendar!');
        } else {
          setSuccess('✅ Booking updated successfully!');
        }
      }
      
      return result;
    } catch (error) {
      setLocalError(error.message);
      console.error('❌ Booking update failed:', error);
      throw error;
    }
  };

  const handleDeleteBooking = async (customerId, bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }

    try {
      setLocalError(null);
      setSuccess(null);

      console.log('🗑️ Deleting booking with calendar cleanup...');

      // Enhanced booking deletion with calendar cleanup
      const result = await deleteBooking(customerId, bookingId);
      
      if (result.success) {
        // Enhanced success message based on calendar integration
        if (isCalendarIntegrated) {
          setSuccess('✅ Booking deleted and removed from Google Calendar!');
        } else {
          setSuccess('✅ Booking deleted successfully!');
        }
      }
      
      return result;
    } catch (error) {
      setLocalError(error.message);
      console.error('❌ Booking deletion failed:', error);
      throw error;
    }
  };

  // ==========================================
  // EXISTING CONFLICT RESOLUTION FUNCTIONS (PRESERVED)
  // ==========================================

  const handleCreateBookingWithConflictCheck = async (bookingData) => {
    try {
      if (!conflictResolutionEnabled) {
        return await handleCreateBooking(bookingData);
      }

      const result = await createBookingWithConflictResolution(bookingData);
      
      if (result.hasConflicts) {
        setConflicts(result.conflicts);
        setSuggestedAlternatives(result.suggestedAlternatives);
        setConflictBookingData(bookingData);
        setShowConflictModal(true);
        return { success: false, hasConflicts: true };
      }
      
      return await handleCreateBooking(bookingData);
    } catch (error) {
      setLocalError(error.message);
      throw error;
    }
  };

  const handleConflictResolution = async (selectedAlternative) => {
    try {
      const updatedBookingData = {
        ...conflictBookingData,
        bookingDate: selectedAlternative.date,
        time: selectedAlternative.time
      };
      
      const result = await handleCreateBooking(updatedBookingData);
      
      setShowConflictModal(false);
      setConflicts([]);
      setSuggestedAlternatives([]);
      setConflictBookingData(null);
      
      return result;
    } catch (error) {
      setLocalError(error.message);
      throw error;
    }
  };

  const handleForceCreateBooking = async () => {
    try {
      const result = await handleCreateBooking(conflictBookingData);
      
      setShowConflictModal(false);
      setConflicts([]);
      setSuggestedAlternatives([]);
      setConflictBookingData(null);
      
      return result;
    } catch (error) {
      setLocalError(error.message);
      throw error;
    }
  };

  // ==========================================
  // CALENDAR VIEW INTEGRATION FUNCTIONS (PRESERVED)
  // ==========================================

  const handleCalendarDateSelect = useCallback((date) => {
    setSelectedDate(date);
    console.log('Calendar date selected:', date);
  }, []);

  const handleCalendarBookingClick = useCallback((booking) => {
    console.log('Calendar booking clicked:', booking);
    setEditingBooking(booking);
  }, []);

  const handleCalendarCreateBooking = useCallback((date) => {
    console.log('Create booking from calendar for date:', date);
    setSelectedDate(date);
    setShowCreateModal(true);
  }, []);

  // ==========================================
  // FILTERING AND SEARCH (PRESERVED WITH REAL-TIME DATA)
  // ==========================================
  
  useEffect(() => {
    let filtered = [...bookings]; // Use real-time bookings from unified hook

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.customer_name?.toLowerCase().includes(search) ||
        booking.service_name?.toLowerCase().includes(search) ||
        booking.customer_whatsapp?.includes(search) ||
        booking.customerEmail?.toLowerCase().includes(search)
      );
    }

    // Apply date filter for calendar view
    if (view === 'calendar' && selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      filtered = filtered.filter(booking => booking.booking_date === dateStr);
    }

    setFilteredBookings(filtered);
  }, [bookings, statusFilter, searchTerm, selectedDate, view]);

  // ==========================================
  // LOAD CALENDAR URLS EFFECT
  // ==========================================
  
  useEffect(() => {
    const loadCalendarUrls = async () => {
      if (isCalendarIntegrated) {
        try {
          const urls = await getCalendarSubscriptionURLs();
          setCalendarUrls(urls);
        } catch (error) {
          console.error('Failed to load calendar URLs:', error);
        }
      }
    };
    
    loadCalendarUrls();
  }, [isCalendarIntegrated, getCalendarSubscriptionURLs]);

  // ==========================================
  // FILTERS AND CONTROLS
  // ==========================================
  
  const renderFilters = () => (
    <div className="filters flex gap-4 mb-6">
      <div className="search-box flex-1">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <option value="all">All Status</option>
        <option value="confirmed">Confirmed</option>
        <option value="pending">Pending</option>
        <option value="rescheduled">Rescheduled</option>
        <option value="cancelled">Cancelled</option>
      </select>
      
      <button
        onClick={() => setShowCreateModal(true)}
        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
      >
        <Plus className="w-4 h-4" />
        New Booking
      </button>
    </div>
  );

  // ==========================================
  // ENHANCED BOOKINGS LIST RENDER FUNCTION (NEW)
  // ==========================================
  
  const renderBookingsList = () => {
    if (bookingsLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading bookings...</span>
        </div>
      );
    }

    if (bookingsError) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Bookings</h3>
          <p className="text-gray-600 mb-4">{bookingsError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      );
    }

    const filtered = bookings.filter(booking => {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const customerName = (booking.customer_name || booking.customerName || '').toLowerCase();
        const serviceName = (booking.service_name || booking.serviceName || '').toLowerCase();
        const phone = (booking.customer_whatsapp || booking.customerPhone || '').toLowerCase();
        
        if (!customerName.includes(searchLower) && 
            !serviceName.includes(searchLower) && 
            !phone.includes(searchLower)) {
          return false;
        }
      }

      // Apply status filter
      if (statusFilter !== 'all' && booking.status !== statusFilter) {
        return false;
      }

      return true;
    });

    if (filtered.length === 0) {
      return (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookings Found</h3>
          <p className="text-gray-600 mb-4">
            {bookings.length === 0 
              ? "You don't have any bookings yet. Create your first booking to get started!" 
              : "No bookings match your current filters. Try adjusting your search or filter criteria."
            }
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center mx-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create First Booking
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {filtered.map(renderBookingCard)}
      </div>
    );
  };

  // ==========================================
  // BOOKING LIST VIEW (ENHANCED WITH SYNC STATUS)
  // ==========================================
  
  const renderBookingListView = () => (
    <div className="booking-list-view">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">
            Bookings ({filteredBookings.length})
            {isCalendarIntegrated && (
              <span className="ml-2 text-sm text-green-600 font-normal">
                🔄 Synced with Calendar
              </span>
            )}
          </h3>
          
          {/* ENHANCED: Use the new renderBookingsList function */}
          {renderBookingsList()}
        </div>
      </div>
    </div>
  );

  // ==========================================
  // CALENDAR VIEW RENDER (ENHANCED)
  // ==========================================
  
  const renderBookingCalendarView = () => (
    <div className="booking-calendar-view">
      <CalendarView
        bookings={filteredBookings}
        events={calendarEvents || filteredBookings} // Use calendar events OR bookings
        onDateSelect={handleCalendarDateSelect}
        onBookingClick={handleCalendarBookingClick}
        onCreateBooking={handleCalendarCreateBooking}
        selectedDate={stableSelectedDate}
        calendarIntegrated={isCalendarIntegrated}
        showIntegrationPrompt={!isCalendarIntegrated}
        loading={bookingsLoading}
      />
    </div>
  );

  // ==========================================
  // DEBUG INFO COMPONENT (NEW)
  // ==========================================
  
  const renderDebugInfo = () => {
    if (process.env.NODE_ENV !== 'development') return null;
    
    return (
      <div className="mt-8 p-4 bg-gray-100 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Debug Information</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>Total bookings: {bookings.length}</p>
          <p>Loading: {bookingsLoading.toString()}</p>
          <p>Error: {bookingsError || 'None'}</p>
          <p>Calendar integrated: {isCalendarIntegrated.toString()}</p>
          <p>Calendar status: {calendarStatus}</p>
          <p>Services available: {services.length}</p>
          {bookings.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer font-medium">Sample Booking Data</summary>
              <pre className="mt-2 text-xs bg-white p-2 rounded overflow-x-auto">
                {JSON.stringify(bookings[0], null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  };

  // ==========================================
  // MAIN RENDER
  // ==========================================
  
  if (bookingsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2">Loading bookings...</span>
      </div>
    );
  }

  return (
    <div className="booking-manager">
      {/* Calendar Integration Status */}
      {renderCalendarIntegrationStatus()}
      
      {/* Error Display */}
      {(bookingsError || localError || createError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-red-700">{bookingsError || localError || createError}</span>
          </div>
        </div>
      )}
      
      {/* Success Display */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-green-700">{success}</span>
            </div>
            <button 
              onClick={() => setSuccess(null)}
              className="text-green-500 hover:text-green-700"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* View Toggle */}
      {renderViewToggle()}
      
      {/* Filters */}
      {renderFilters()}
      
      {/* Main Content */}
      {view === 'list' && renderBookingListView()}
      {view === 'calendar' && renderBookingCalendarView()}
      
      {/* Debug Information (Development Only) */}
      {renderDebugInfo()}
      
      {/* ==========================================
           EXISTING MODALS (PRESERVED)
           ========================================== */}
      
      {showCreateModal && (
        <CreateBookingModal
          businessId={user?.businessId}
          services={services}
          selectedDate={stableSelectedDate}
          onClose={() => setShowCreateModal(false)}
          onSubmit={conflictResolutionEnabled ? handleCreateBookingWithConflictCheck : handleCreateBooking}
          loading={createLoading}
        />
      )}

      {editingBooking && (
        <EditBookingModal
          booking={editingBooking}
          services={services}
          onClose={() => setEditingBooking(null)}
          onSubmit={(updateData) => handleUpdateBooking(
            editingBooking.customerId, 
            editingBooking.id, 
            updateData
          )}
        />
      )}

      {showConflictModal && (
        <ConflictResolutionModal
          conflicts={conflicts}
          suggestedAlternatives={suggestedAlternatives}
          originalBookingData={conflictBookingData}
          onSelectAlternative={handleConflictResolution}
          onForceCreate={handleForceCreateBooking}
          onClose={() => {
            setShowConflictModal(false);
            setConflicts([]);
            setSuggestedAlternatives([]);
            setConflictBookingData(null);
          }}
          loading={conflictLoading}
        />
      )}
    </div>
  );
};

// ==========================================
// EXISTING MODAL COMPONENTS (PRESERVED)
// ==========================================

// Conflict Resolution Modal Component (PRESERVED)
const ConflictResolutionModal = ({ 
  conflicts, 
  suggestedAlternatives, 
  originalBookingData, 
  onSelectAlternative, 
  onForceCreate, 
  onClose, 
  loading 
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-lg w-full p-6">
        <h2 className="text-xl font-bold mb-4 text-red-600">
          <AlertCircle className="inline w-5 h-5 mr-2" />
          Booking Conflict Detected
        </h2>
        
        <div className="mb-6">
          <p className="text-gray-700 mb-4">
            The requested time slot conflicts with existing bookings:
          </p>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            {conflicts.map((conflict, index) => (
              <div key={index} className="text-sm text-red-700">
                • {conflict.customer_name} - {conflict.service_name} at {conflict.time}
              </div>
            ))}
          </div>
          
          {suggestedAlternatives.length > 0 && (
            <div>
              <p className="font-medium mb-3">Suggested alternative times:</p>
              <div className="space-y-2">
                {suggestedAlternatives.map((alternative, index) => (
                  <button
                    key={index}
                    onClick={() => onSelectAlternative(alternative)}
                    className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300"
                  >
                    {alternative.date} at {alternative.time}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onForceCreate}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {loading ? 'Creating...' : 'Force Create Anyway'}
          </button>
        </div>

        {loading && (
          <div className="mt-4 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Processing...</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Create Booking Modal Component (ENHANCED)
const CreateBookingModal = ({ businessId, services, selectedDate, onClose, onSubmit, loading }) => {
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    serviceName: '',
    servicePrice: '',
    duration: '60',
    bookingDate: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
    time: '',
    notes: ''
  });

  useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        bookingDate: selectedDate.toISOString().split('T')[0]
      }));
    }
  }, [selectedDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      notify_customer: notifyCustomer
    });
  };

  const handleServiceChange = (serviceName) => {
    const service = services.find(s => s.name === serviceName);
    setFormData(prev => ({
      ...prev,
      serviceName,
      servicePrice: service?.price || '',
      duration: service?.duration || '60'
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Create New Booking</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name *
            </label>
            <input
              type="text"
              required
              value={formData.customerName}
              onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={formData.customerPhone}
              onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email (Optional)
            </label>
            <input
              type="email"
              value={formData.customerEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service *
            </label>
            <select
              required
              value={formData.serviceName}
              onChange={(e) => handleServiceChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a service</option>
              {services.map((service) => (
                <option key={service.id} value={service.name}>
                  {service.name} - ${service.price} ({service.duration}min)
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price *
              </label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.servicePrice}
                onChange={(e) => setFormData(prev => ({ ...prev, servicePrice: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (min) *
              </label>
              <input
                type="number"
                required
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.bookingDate}
                onChange={(e) => setFormData(prev => ({ ...prev, bookingDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time *
              </label>
              <input
                type="time"
                required
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <input
              type="checkbox"
              id="bm-notifyCustomer"
              checked={notifyCustomer}
              onChange={(e) => setNotifyCustomer(e.target.checked)}
            />
            <label htmlFor="bm-notifyCustomer" style={{ fontSize: '14px', cursor: 'pointer' }}>
              Send WhatsApp confirmation to customer
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Booking Modal Component (PRESERVED)
const EditBookingModal = ({ booking, services, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    customerName: booking.customer_name || '',
    customerPhone: booking.customer_whatsapp || '',
    customerEmail: booking.customerEmail || '',
    serviceName: booking.service_name || '',
    servicePrice: booking.service_price || '',
    duration: booking.duration_minutes || '60',
    bookingDate: booking.booking_date || '',
    time: booking.time || '',
    notes: booking.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleServiceChange = (serviceName) => {
    const service = services.find(s => s.name === serviceName);
    setFormData(prev => ({
      ...prev,
      serviceName,
      servicePrice: service?.price || prev.servicePrice,
      duration: service?.duration || prev.duration
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <h2 className="text-xl font-bold mb-4">Edit Booking</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer Name *
            </label>
            <input
              type="text"
              required
              value={formData.customerName}
              onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={formData.customerPhone}
              onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email (Optional)
            </label>
            <input
              type="email"
              value={formData.customerEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service *
            </label>
            <select
              required
              value={formData.serviceName}
              onChange={(e) => handleServiceChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a service</option>
              {services.map((service) => (
                <option key={service.id} value={service.name}>
                  {service.name} - ${service.price} ({service.duration}min)
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price *
              </label>
              <input
                type="number"
                required
                step="0.01"
                value={formData.servicePrice}
                onChange={(e) => setFormData(prev => ({ ...prev, servicePrice: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (min) *
              </label>
              <input
                type="number"
                required
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                required
                value={formData.bookingDate}
                onChange={(e) => setFormData(prev => ({ ...prev, bookingDate: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time *
              </label>
              <input
                type="time"
                required
                value={formData.time}
                onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows="3"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Update Booking
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingManager;
